<template>
  <div class="bg-white shadow-sm rounded-lg overflow-hidden">
    <!-- Header -->
    <div class="px-3 py-2 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
      <h3 class="text-xs font-medium text-gray-700 uppercase tracking-wide">
        Base
      </h3>
      <button
        v-if="!loading && !error"
        @click="$emit('refresh')"
        class="inline-flex items-center text-xs text-gray-600 hover:text-gray-800"
        title="Actualiser"
      >
        <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="px-3 py-6 text-center">
      <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-400 mx-auto"></div>
      <p class="mt-2 text-xs text-gray-500">Chargement...</p>
    </div>

    <!-- Suggestions List -->
    <div v-else-if="suggestions && suggestions.length > 0" class="divide-y divide-gray-200">
      <div
        v-for="(suggestion, index) in suggestions"
        :key="suggestion.id"
        class="p-3 hover:bg-gray-50 transition-colors cursor-pointer"
        @click="toggleExpanded(suggestion.id)"
      >
        <div class="flex items-start justify-between gap-2">
          <div class="flex-1 min-w-0">
            <div class="flex items-center space-x-2">
              <span class="inline-flex items-center justify-center h-5 w-5 rounded-full bg-gray-100 text-gray-700 text-xs font-medium flex-shrink-0">
                {{ index + 1 }}
              </span>
              <h4 class="text-xs font-medium text-gray-900">{{ suggestion.title }}</h4>
            </div>

            <div class="mt-1.5 flex items-center space-x-2 text-xs text-gray-500">
              <span v-if="suggestion.product_type" class="inline-flex items-center px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200">
                {{ suggestion.product_type }}
              </span>
              <span v-if="suggestion.category" class="inline-flex items-center px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200">
                {{ suggestion.category }}
              </span>
              <span v-if="suggestion.priority" class="text-gray-500">
                P{{ suggestion.priority }}
              </span>
            </div>

            <div v-if="suggestion.keywords && suggestion.keywords.length > 0" class="mt-1.5 flex flex-wrap gap-1">
              <span
                v-for="keyword in suggestion.keywords.slice(0, 3)"
                :key="keyword"
                class="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600"
              >
                {{ keyword }}
              </span>
            </div>
          </div>

          <div class="flex items-center gap-1 flex-shrink-0">
            <!-- Edit Button -->
            <button
              @click.stop="openEditModal(suggestion)"
              class="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-blue-600 hover:text-white hover:border-blue-600 focus:outline-none transition-colors"
              title="Éditer"
            >
              <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>

            <!-- Use Response Button -->
            <button
              @click.stop="$emit('use-response', suggestion.content, suggestion.id)"
              class="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-900 hover:text-white hover:border-gray-900 focus:outline-none transition-colors"
              title="Utiliser cette réponse"
            >
              <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
            </button>

            <!-- Expand Icon -->
            <button
              class="inline-flex items-center justify-center h-6 w-6 text-gray-400 hover:text-gray-600 transition-colors"
              title="Voir le détail"
            >
              <svg
                class="h-4 w-4 transition-transform"
                :class="{ 'transform rotate-180': expandedIds.includes(suggestion.id) }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Expanded Content -->
        <div v-if="expandedIds.includes(suggestion.id)" class="mt-4 pt-4 border-t border-gray-200">
          <div class="prose prose-sm max-w-none" v-html="renderMarkdown(suggestion.content)"></div>
          <div class="mt-4 flex space-x-2">
            <button
              @click.stop="$emit('use-response', suggestion.content, suggestion.id)"
              class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              Utiliser cette réponse
            </button>
            <button
              @click.stop="$emit('insert-snippet', suggestion.content, suggestion.id)"
              class="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
              Insérer un extrait
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else-if="!loading && !error" class="px-4 py-12 text-center">
      <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
      <h3 class="mt-2 text-sm font-medium text-gray-900">Aucune suggestion</h3>
      <p class="mt-1 text-sm text-gray-500">Aucune réponse correspondante dans la base de connaissances.</p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="px-4 py-8 text-center">
      <svg class="mx-auto h-12 w-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
      <p class="mt-2 text-sm text-red-600">{{ error }}</p>
    </div>

    <!-- Modal d'édition -->
    <div v-if="showEditModal" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="edit-modal-title" role="dialog" aria-modal="true">
      <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <!-- Background overlay -->
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="closeEditModal"></div>

        <!-- Modal panel -->
        <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="mb-4">
              <h3 class="text-base font-medium text-gray-900" id="edit-modal-title">
                Éditer la réponse KB
              </h3>
              <p class="mt-1 text-xs text-gray-500">
                Modifier les informations de la réponse dans la base de connaissances
              </p>
            </div>

            <!-- Error message -->
            <div v-if="editError" class="mb-4 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
              {{ editError }}
            </div>

            <div class="space-y-3">
              <!-- Title -->
              <div>
                <label class="block text-xs font-medium text-gray-700 mb-1">
                  Titre <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="editForm.title"
                  type="text"
                  class="block w-full rounded border-gray-300 shadow-sm focus:border-gray-400 focus:ring-gray-200 text-sm"
                  placeholder="Ex: Installation Docker sur VPS"
                />
              </div>

              <!-- Content -->
              <div>
                <label class="block text-xs font-medium text-gray-700 mb-1">
                  Contenu <span class="text-red-500">*</span>
                </label>
                <textarea
                  v-model="editForm.content"
                  rows="12"
                  class="block w-full rounded border-gray-300 shadow-sm focus:border-gray-400 focus:ring-gray-200 text-sm font-mono"
                  placeholder="Contenu en Markdown..."
                ></textarea>
                <p class="mt-1 text-xs text-gray-500">
                  {{ editForm.content.length }} caractères - Format Markdown supporté
                </p>
              </div>

              <!-- Department -->
              <div>
                <label class="block text-xs font-medium text-gray-700 mb-1">
                  Département
                </label>
                <input
                  v-model="editForm.deptname"
                  type="text"
                  class="block w-full rounded border-gray-300 shadow-sm focus:border-gray-400 focus:ring-gray-200 text-sm"
                  placeholder="Ex: Support Technique"
                />
              </div>

              <div class="grid grid-cols-2 gap-3">
                <!-- Priority -->
                <div>
                  <label class="block text-xs font-medium text-gray-700 mb-1">
                    Priorité
                  </label>
                  <input
                    v-model.number="editForm.priority"
                    type="number"
                    min="0"
                    class="block w-full rounded border-gray-300 shadow-sm focus:border-gray-400 focus:ring-gray-200 text-sm"
                    placeholder="0"
                  />
                </div>

                <!-- Active -->
                <div class="flex items-center">
                  <label class="inline-flex items-center">
                    <input
                      v-model="editForm.is_active"
                      type="checkbox"
                      class="rounded border-gray-300 text-gray-900 shadow-sm focus:border-gray-400 focus:ring focus:ring-gray-200 focus:ring-opacity-50"
                    >
                    <span class="ml-2 text-xs text-gray-700">Réponse active</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              @click="saveEdit"
              :disabled="savingEdit"
              type="button"
              class="w-full inline-flex justify-center rounded border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-xs font-medium text-white hover:bg-blue-700 focus:outline-none sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg v-if="savingEdit" class="animate-spin h-3 w-3 mr-2" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ savingEdit ? 'Sauvegarde...' : 'Sauvegarder' }}
            </button>
            <button
              @click="closeEditModal"
              :disabled="savingEdit"
              type="button"
              class="mt-3 w-full inline-flex justify-center rounded border border-gray-300 shadow-sm px-4 py-2 bg-white text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Annuler
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { marked } from 'marked';

export default {
  name: 'SuggestionsKB',
  props: {
    suggestions: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      expandedIds: [],
      showEditModal: false,
      editingSuggestion: null,
      editForm: {
        title: '',
        content: '',
        deptname: '',
        priority: 0,
        is_active: true
      },
      savingEdit: false,
      editError: null
    };
  },
  methods: {
    toggleExpanded(id) {
      const index = this.expandedIds.indexOf(id);
      if (index > -1) {
        this.expandedIds.splice(index, 1);
      } else {
        this.expandedIds.push(id);
      }
    },
    getScoreColor(score) {
      if (score >= 80) return 'text-green-600';
      if (score >= 60) return 'text-yellow-600';
      return 'text-orange-600';
    },
    renderMarkdown(text) {
      if (!text) return '';
      return marked(text);
    },

    async openEditModal(suggestion) {
      this.editingSuggestion = suggestion;
      this.editForm = {
        title: suggestion.title,
        content: suggestion.content,
        deptname: suggestion.deptname || '',
        priority: suggestion.priority || 0,
        is_active: suggestion.is_active !== undefined ? suggestion.is_active : true
      };
      this.editError = null;
      this.showEditModal = true;
    },

    closeEditModal() {
      this.showEditModal = false;
      this.editingSuggestion = null;
      this.editError = null;
    },

    async saveEdit() {
      if (!this.editForm.title.trim()) {
        this.editError = 'Le titre est requis';
        return;
      }

      if (!this.editForm.content.trim()) {
        this.editError = 'Le contenu ne peut pas être vide';
        return;
      }

      this.savingEdit = true;
      this.editError = null;

      try {
        const payload = {
          deptname: this.editForm.deptname || null,
          title: this.editForm.title,
          content: this.editForm.content,
          priority: this.editForm.priority,
          is_active: this.editForm.is_active
        };

        // Émettre l'événement de sauvegarde au parent
        this.$emit('update-suggestion', this.editingSuggestion.id, payload);

        this.closeEditModal();
      } catch (error) {
        console.error('Error saving edit:', error);
        this.editError = error.message || 'Erreur lors de la sauvegarde';
      } finally {
        this.savingEdit = false;
      }
    }
  }
};
</script>

<style scoped>
.prose :deep(h1) { @apply text-base font-bold mb-2 mt-4; }
.prose :deep(h2) { @apply text-sm font-bold mb-2 mt-3; }
.prose :deep(h3) { @apply text-sm font-semibold mb-1 mt-2; }
.prose :deep(p) { @apply mb-2 text-sm; }
.prose :deep(ul) { @apply list-disc pl-5 mb-2 text-sm; }
.prose :deep(ol) { @apply list-decimal pl-5 mb-2 text-sm; }
.prose :deep(code) { @apply bg-gray-100 px-1 py-0.5 rounded text-xs font-mono; }
.prose :deep(pre) { @apply bg-gray-100 p-3 rounded text-xs overflow-x-auto mb-2; }
.prose :deep(pre code) { @apply bg-transparent p-0; }
.prose :deep(strong) { @apply font-bold; }
.prose :deep(a) { @apply text-indigo-600 hover:text-indigo-800 underline; }
</style>
